import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import type { AppDispatch, RootState } from "configureStore";
import {
  fetchBoards,
  fetchBoardsHome,
  starBoardApi,
  pinBoardApi,
} from "./project-boards.service";
import {
  BoardCard,
  BoardsHomeSections,
  BoardsPagination,
  ViewMode,
} from "./types";

const VIEW_MODE_KEY = "project-boards-view";
const ALL_PAGE_SIZE = 24;

function readViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
}

interface BoardsState {
  sections: BoardsHomeSections;
  sectionsLoading: boolean;
  sectionsRequestId: number;
  all: BoardCard[];
  allPagination: BoardsPagination | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  search: string;
  viewMode: ViewMode;
  // Generation token for the "All" list. Bumped whenever a page-1 load starts
  // (search/company change). Page responses carry the token they were issued
  // under and are dropped if it no longer matches — so a slow next-page from a
  // previous query can't append stale rows into the refreshed list.
  allRequestId: number;
}

const initialState: BoardsState = {
  sections: { pinned: [], starred: [], recent: [] },
  sectionsLoading: false,
  sectionsRequestId: 0,
  all: [],
  allPagination: null,
  isLoading: false,
  isLoadingMore: false,
  isError: false,
  search: "",
  viewMode: readViewMode(),
  allRequestId: 0,
};

let sectionsLoadSeq = 0;
function nextSectionsLoadId(): number {
  sectionsLoadSeq += 1;
  return sectionsLoadSeq;
}

let allLoadSeq = 0;
function nextAllLoadId(): number {
  allLoadSeq += 1;
  return allLoadSeq;
}

// Update a flag for a board wherever it appears (the full list + every section).
function setBoardFlag(
  state: BoardsState,
  boardId: string,
  field: "isStarred" | "isPinned",
  value: boolean
): void {
  const apply = (arr: BoardCard[]) =>
    arr.forEach((b) => {
      if (b.id === boardId) b[field] = value;
    });
  apply(state.all);
  apply(state.sections.pinned);
  apply(state.sections.starred);
  apply(state.sections.recent);
}

const slice = createSlice({
  name: "projectboards",
  initialState,
  reducers: {
    startSectionsLoad(state, a: PayloadAction<number>) {
      state.sectionsRequestId = a.payload;
      state.sectionsLoading = true;
    },
    finishSectionsLoad(state, a: PayloadAction<number>) {
      if (a.payload !== state.sectionsRequestId) return;
      state.sectionsLoading = false;
    },
    setSections(
      state,
      a: PayloadAction<{ sections: BoardsHomeSections; requestId: number }>
    ) {
      if (a.payload.requestId !== state.sectionsRequestId) return;
      state.sections = a.payload.sections;
      state.sectionsLoading = false;
    },
    setLoadingMore(state, a: PayloadAction<boolean>) {
      state.isLoadingMore = a.payload;
    },
    // Begin a fresh page-1 load: bump the generation token and drop the old
    // pagination so the infinite-scroll sentinel can't fire a next-page off the
    // previous query's page index while the refresh is in flight. The existing
    // rows stay visible until page 1 lands (no skeleton flash on a search edit).
    startAllLoad(state, a: PayloadAction<number>) {
      state.allRequestId = a.payload;
      state.isLoading = true;
      state.isLoadingMore = false;
      state.isError = false;
      state.allPagination = null;
    },
    setError(state, a: PayloadAction<number | undefined>) {
      if (a.payload !== undefined && a.payload !== state.allRequestId) return;
      state.isError = true;
      state.isLoading = false;
      state.isLoadingMore = false;
    },
    setAllFirstPage(
      state,
      a: PayloadAction<{
        collection: BoardCard[];
        pagination: BoardsPagination;
        requestId: number;
      }>
    ) {
      if (a.payload.requestId !== state.allRequestId) return;
      state.all = a.payload.collection;
      state.allPagination = a.payload.pagination;
      state.isLoading = false;
      state.isError = false;
    },
    appendAll(
      state,
      a: PayloadAction<{
        collection: BoardCard[];
        pagination: BoardsPagination;
        requestId: number;
      }>
    ) {
      // Always release the loading-more latch, even for a stale response, so a
      // superseded next-page can't leave the sentinel permanently blocked.
      state.isLoadingMore = false;
      if (a.payload.requestId !== state.allRequestId) return;
      const seen = new Set(state.all.map((b) => b.id));
      a.payload.collection.forEach((b) => {
        if (!seen.has(b.id)) state.all.push(b);
      });
      state.allPagination = a.payload.pagination;
    },
    setSearch(state, a: PayloadAction<string>) {
      state.search = a.payload;
    },
    // Clean slate for a company switch: drop the previous company's boards,
    // sections, pagination and search so none of it can show (or be clicked)
    // under the new company URL while the new data loads. The view-mode
    // preference is intentionally preserved. isLoading is set so the All list
    // shows its skeleton immediately instead of a one-frame "no boards" flash.
    resetBoards(state) {
      state.all = [];
      state.allPagination = null;
      state.sections = { pinned: [], starred: [], recent: [] };
      state.sectionsRequestId += 1;
      state.sectionsLoading = true;
      state.search = "";
      state.isLoading = true;
      state.isLoadingMore = false;
      state.isError = false;
    },
    setViewMode(state, a: PayloadAction<ViewMode>) {
      state.viewMode = a.payload;
      try {
        localStorage.setItem(VIEW_MODE_KEY, a.payload);
      } catch {
        /* ignore */
      }
    },
    applyStar(state, a: PayloadAction<{ board: BoardCard; isStarred: boolean }>) {
      const { board, isStarred } = a.payload;
      setBoardFlag(state, board.id, "isStarred", isStarred);
      state.sections.starred = state.sections.starred.filter(
        (b) => b.id !== board.id
      );
      if (isStarred) {
        state.sections.starred.push({ ...board, isStarred: true });
        state.sections.starred.sort((x, y) => x.name.localeCompare(y.name));
      }
    },
    applyPin(state, a: PayloadAction<{ board: BoardCard; isPinned: boolean }>) {
      const { board, isPinned } = a.payload;
      setBoardFlag(state, board.id, "isPinned", isPinned);
      state.sections.pinned = state.sections.pinned.filter(
        (b) => b.id !== board.id
      );
      if (isPinned) {
        state.sections.pinned.push({ ...board, isPinned: true });
        state.sections.pinned.sort((x, y) => x.name.localeCompare(y.name));
      }
    },
  },
});

export const {
  startSectionsLoad,
  finishSectionsLoad,
  setSections,
  setLoadingMore,
  startAllLoad,
  setError,
  setAllFirstPage,
  appendAll,
  setSearch,
  resetBoards,
  setViewMode,
  applyStar,
  applyPin,
} = slice.actions;

export default slice.reducer;

// --- thunks ----------------------------------------------------------------

export const fetchBoardsHomeThunk =
  (companyId: string) => async (dispatch: AppDispatch) => {
    const requestId = nextSectionsLoadId();
    dispatch(startSectionsLoad(requestId));
    try {
      const sections = await fetchBoardsHome(companyId);
      dispatch(setSections({ sections, requestId }));
    } catch (e) {
      dispatch(finishSectionsLoad(requestId));
    }
  };

export const fetchAllFirstPage =
  (companyId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const requestId = nextAllLoadId();
    dispatch(startAllLoad(requestId));
    try {
      const { search } = getState().projectBoardsReducer;
      const data = await fetchBoards(companyId, {
        search: search || undefined,
        page: 1,
        pageSize: ALL_PAGE_SIZE,
      });
      dispatch(setAllFirstPage({ ...data, requestId }));
    } catch (e) {
      dispatch(setError(requestId));
    }
  };

export const fetchAllNextPage =
  (companyId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { allPagination, isLoadingMore, isLoading, search, allRequestId } =
      getState().projectBoardsReducer;
    // Don't page while a page-1 refresh is in flight: allPagination still holds
    // the previous query's index and the list is about to be replaced.
    if (
      !allPagination ||
      !allPagination.hasNextPage ||
      isLoadingMore ||
      isLoading
    )
      return;
    const requestId = allRequestId;
    dispatch(setLoadingMore(true));
    try {
      const data = await fetchBoards(companyId, {
        search: search || undefined,
        page: allPagination.page + 1,
        pageSize: allPagination.pageSize,
      });
      dispatch(appendAll({ ...data, requestId }));
    } catch (e) {
      dispatch(setLoadingMore(false));
    }
  };

export const toggleStar =
  (companyId: string, board: BoardCard) => async (dispatch: AppDispatch) => {
    const next = !board.isStarred;
    dispatch(applyStar({ board, isStarred: next })); // optimistic
    try {
      const { isStarred } = await starBoardApi(companyId, board.id);
      if (isStarred !== next) dispatch(applyStar({ board, isStarred }));
    } catch (e) {
      dispatch(applyStar({ board, isStarred: !next })); // revert
      toast.error("Could not update star");
    }
  };

export const togglePin =
  (companyId: string, board: BoardCard) => async (dispatch: AppDispatch) => {
    const next = !board.isPinned;
    dispatch(applyPin({ board, isPinned: next })); // optimistic
    try {
      const { isPinned } = await pinBoardApi(companyId, board.id);
      if (isPinned !== next) dispatch(applyPin({ board, isPinned }));
    } catch (e) {
      dispatch(applyPin({ board, isPinned: !next })); // revert
      toast.error("Could not update pin");
    }
  };

/** Compat: other modules (board create/edit form) dispatch this to refresh. */
export const fetchProjectBoards =
  (companyId: string) => async (dispatch: AppDispatch) => {
    dispatch(fetchBoardsHomeThunk(companyId));
    dispatch(fetchAllFirstPage(companyId));
  };
